// Merchant Product utilities for displaying merchant product information
// Shared across mobile and desktop views with configurable options

export interface MerchantProduct {
  sku?: string;
  name?: string;
  id?: string;
}

export interface MerchantProductDisplayOptions {
  maxLength?: number;
  prefix?: string;
}

export interface MerchantProductDisplayResult {
  value: string;
  isClickable: boolean;
  fullValue: string;
  needsTruncation: boolean;
}

/**
 * Get merchant product display value with configurable options
 * 
 * @param merchantProduct - The merchant product data
 * @param options - Display configuration options
 * @param options.maxLength - Maximum length before truncation (default: 25)
 * @param options.prefix - Text prefix to add before the value (default: "")
 * @returns Display information including formatted value, clickability, and truncation status
 */
export const getMerchantProductDisplayValue = (
  merchantProduct: MerchantProduct | undefined,
  options: MerchantProductDisplayOptions = {}
): MerchantProductDisplayResult => {
  const { maxLength = 25, prefix = "" } = options;

  if (!merchantProduct) {
    return {
      value: `${prefix}(N/A)`,
      isClickable: false,
      fullValue: "(N/A)",
      needsTruncation: false
    };
  }

  const { sku, name, id } = merchantProduct;
  let fullValue = "";

  // New logic: Combine available fields optimally
  if (sku && name) {
    // Both available: "SKU123 - Product Name"
    fullValue = `${sku} - ${name}`;
  } else if (sku) {
    // Only SKU: "SKU123" (no separator)
    fullValue = sku;
  } else if (name) {
    // Only name: "Product Name" (no separator)
    fullValue = name;
  } else if (id) {
    // ID fallback: "⚠️ 12345..." (warning + first 5 digits + ellipsis)
    const truncatedId = id.substring(0, 5);
    fullValue = `⚠️ ${truncatedId}...`;
  } else {
    return {
      value: `${prefix}(N/A)`,
      isClickable: false,
      fullValue: "(N/A)",
      needsTruncation: false
    };
  }

  // Calculate truncation based on the content length, not including prefix
  const needsTruncation = fullValue.length > maxLength;
  const truncatedValue = needsTruncation
    ? fullValue.substring(0, maxLength - 3) + "..."
    : fullValue;

  return {
    value: `${prefix}${truncatedValue}`,
    isClickable: true,
    fullValue: `${prefix}${fullValue}`,
    needsTruncation: needsTruncation
  };
};